import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState } from "react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { AdminListPage, AdminRow } from "@/components/admin/admin-list-page"
import { AdminLinkedInLink, isSafeHttpUrl } from "@/components/admin/admin-person-line"
import { Badge } from "@/components/ui/badge"
import { StatusPill } from "@/components/status-pill"
import { Button } from "@/components/ui/button"
import { useConfirmAction } from "@/hooks/use-confirm-action"

type RoleFilter = "all" | "seeker" | "employer"

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "seeker", label: "Seekers" },
  { value: "employer", label: "Employers" },
] as const

export const Route = createFileRoute("/admin/people")({
  component: AdminPeoplePage,
})

export function AdminPeoplePage() {
  const [filter, setFilter] = useState<RoleFilter>("all")
  const people = useQuery(
    api.admin.listPeople,
    filter === "all" ? {} : { role: filter },
  )
  const setSeekerPlan = useMutation(api.admin.setSeekerPlan)
  const { confirmAction } = useConfirmAction()

  const togglePlan = (
    profileId: Id<"profiles">,
    name: string,
    active: boolean,
  ) =>
    confirmAction(
      active
        ? `Grant 1 year of access to ${name}?`
        : `Revoke access for ${name}?`,
      () => setSeekerPlan({ profileId, active }),
      {
        success: active ? "Granted 1 year of access" : "Access revoked",
      },
    )

  return (
    <AdminListPage
      title="People"
      description="Browse members and manage seeker annual access."
      filter={{ value: filter, onChange: setFilter, options: FILTER_OPTIONS }}
      data={people}
      emptyCopy="No people match this filter."
      renderRow={(person) => (
        <AdminRow
          key={person._id}
          title={person.name}
          badges={
            <>
              <Badge variant="outline">{person.role}</Badge>
              {person.isAdmin && <Badge>Admin</Badge>}
              {person.role === "seeker" && person.seekerAccessActive && (
                <Badge variant="secondary">
                  {person.seekerPaidUntil
                    ? `Active until ${new Date(person.seekerPaidUntil).toLocaleDateString("en-IN")}`
                    : "Active"}
                </Badge>
              )}
              {person.role === "employer" && person.verificationStatus && (
                <StatusPill
                  domain="verification"
                  status={person.verificationStatus}
                  as="badge"
                />
              )}
            </>
          }
          meta={
            <span className="inline-flex flex-wrap items-center gap-x-1.5">
              <span>{person.email ?? "No email on profile"}</span>
              {person.companyName ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{person.companyName}</span>
                </>
              ) : null}
              {isSafeHttpUrl(person.linkedinUrl) ? (
                <>
                  <span aria-hidden>·</span>
                  <AdminLinkedInLink href={person.linkedinUrl} />
                </>
              ) : null}
            </span>
          }
          actions={
            person.role === "seeker" && (
              <Button
                size="sm"
                variant={person.seekerAccessActive ? "outline" : "default"}
                onClick={() =>
                  void togglePlan(
                    person._id,
                    person.name,
                    !person.seekerAccessActive,
                  )
                }
              >
                {person.seekerAccessActive ? "Revoke access" : "Grant 1 year"}
              </Button>
            )
          }
        />
      )}
    />
  )
}
